import { createFileRoute } from '@tanstack/react-router';
import { use } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderNextNavButton,
  HeaderNav,
  HeaderSidebarTrigger,
  HeaderPrevNavButton,
  useHeaderNav,
  HeaderMobileChatButton,
} from '@/app/ui/components/header';
import { EvaluationTypeLabelsEnum, ClauseScannerAnalysisType } from '@/app/utils/constants';
import { SearchFocusIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { AppContext } from '@/app/appContext';
import { useGetClauseScannerSubcategoryQuery } from '@/app/hooks/useClauseScanner';
import { ClausesDetailPageContent } from '@/app/components/features/evaluations/Clauses/ClausesDetailPageContent';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/_layout/(main)/clauses/$subcategoryId/$clauseId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.tenderHome');
  const tFeature = useTranslations('features.clauses');
  const { tenderPackageId, projectId, subcategoryId, clauseId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { userMeta } = use(AppContext);
  const organizationId = userMeta?.organizationId || '';

  const { data: subCategory } = useGetClauseScannerSubcategoryQuery({
    subCategoryId: subcategoryId,
    resourceId: tenderPackageId,
    type: ClauseScannerAnalysisType.TENDER_PACKAGE,
    enabled: !!organizationId,
  });

  const allClauses = subCategory?.clauses || [];

  const { handlePrevious, handleNext, isPreviousDisabled, isNextDisabled, currentIndex } =
    useHeaderNav(allClauses, clauseId, (id) => ({
      to: '/project/$projectId/tender-packages/$tenderPackageId/clauses/$subcategoryId/$clauseId',
      params: { projectId, tenderPackageId, subcategoryId, clauseId: id },
    }));

  if (!subCategory) return null;

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/tender-packages/$tenderPackageId/clauses"
          params={{ projectId, tenderPackageId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={SearchFocusIcon} />
                {t('title')}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/clauses"
                params={{ projectId, tenderPackageId }}
              >
                {tFeature('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/clauses/$subcategoryId"
                params={{ projectId, tenderPackageId, subcategoryId }}
              >
                <span className="line-clamp-1">{subCategory.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="tabular-nums">#{currentIndex + 1}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderNav>
          <HeaderPrevNavButton onClick={handlePrevious} disabled={isPreviousDisabled} />
          <HeaderNextNavButton onClick={handleNext} disabled={isNextDisabled} />
        </HeaderNav>
        <HeaderSidechatButton />
        <HeaderMobileChatButton />
      </Header>
      <ClausesDetailPageContent
        evaluationId={tenderPackageId}
        type={ClauseScannerAnalysisType.TENDER_PACKAGE}
        currentIndex={currentIndex}
      />
    </>
  );
}
